// PdfPageImages — draw every page of a PDF as a fit-to-width image (pdf.js).
//
// Phones have no usable in-page PDF viewer: iOS Safari draws only page 1 as
// a flat picture, Android Chrome draws nothing, and Chrome's own viewer in a
// 390px frame is all toolbar and thumbnail rail. So the preview sheet renders
// the pages itself: each page is a canvas as wide as the sheet, sharp at the
// device's pixel ratio, and the sheet scrolls through them. pdf.js (and its
// worker) load only when a phone opens a preview.
//
// The legacy build supports older Safari (crew iPhones); eval is off, and the
// PDFs are our own invoices and quotes, fetched with the session.

import { useEffect, useRef, useState } from 'react'

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs')

let pdfjsPromise: Promise<PdfJs> | null = null

export function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
    ]).then(([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = worker.default
      return lib
    }).catch((err) => {
      pdfjsPromise = null
      throw err
    })
  }
  return pdfjsPromise
}

/** Device pixels per CSS pixel for the page images (sharp text, bounded memory). */
const MAX_SCALE = 2.5

export interface PdfPageImagesProps {
  /** The PDF file. */
  blob: Blob
  /** Names each page for screen readers: "Invoice INV-7, page 1 of 2". */
  label: string
  /** Pages could not be drawn (pdf.js failed to load or the file is unreadable). */
  onError: (err: unknown) => void
}

export function PdfPageImages({ blob, label, onError }: PdfPageImagesProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [pages, setPages] = useState<Array<{ key: string; canvas: HTMLCanvasElement }>>([])
  const [total, setTotal] = useState(0)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let cancelled = false
    let destroy: (() => void) | null = null
    void (async () => {
      try {
        const [lib, bytes] = await Promise.all([loadPdfJs(), blob.arrayBuffer()])
        const doc = await lib.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false }).promise
        destroy = () => { void doc.destroy() }
        if (cancelled) return destroy()
        setTotal(doc.numPages)
        const cssWidth = Math.max(240, wrapRef.current?.clientWidth || window.innerWidth - 32)
        const dpr = Math.min(MAX_SCALE, window.devicePixelRatio || 1)
        const out: Array<{ key: string; canvas: HTMLCanvasElement }> = []
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr })
          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('This browser can’t draw the PDF')
          // Paint white first: a transparent canvas reads as a black page
          // on a dark-mode screen. The customer copy is always light.
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          await page.render({ canvasContext: ctx, viewport, background: '#FFFFFF' } as Parameters<typeof page.render>[0]).promise
          if (cancelled) return
          out.push({ key: `p${n}`, canvas })
          // Show page 1 as soon as it's drawn; the rest follow.
          setPages([...out])
        }
      } catch (err) {
        if (!cancelled) onErrorRef.current(err)
      }
    })()
    return () => {
      cancelled = true
      destroy?.()
    }
  }, [blob])

  return (
    <div ref={wrapRef} className="flex flex-col gap-3" data-testid="pdf-pages" style={{ colorScheme: 'light' }}>
      {pages.map((p, i) => (
        <CanvasImage key={p.key} canvas={p.canvas} label={`${label}, page ${i + 1}${total > 1 ? ` of ${total}` : ''}`} />
      ))}
    </div>
  )
}

/** Puts an already-drawn canvas in the page as a fit-to-width image. */
function CanvasImage({ canvas, label }: { canvas: HTMLCanvasElement; label: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const host = ref.current
    if (!host) return
    canvas.setAttribute('role', 'img')
    canvas.setAttribute('aria-label', label)
    canvas.style.width = '100%'
    canvas.style.height = 'auto'
    canvas.style.display = 'block'
    canvas.style.background = '#FFFFFF'
    host.replaceChildren(canvas)
  }, [canvas, label])
  return <div ref={ref} className="overflow-hidden" style={{ borderRadius: 8, background: '#FFFFFF' }} />
}
